import http from "../api/apiClient";
import { createLdsMemoryCache, createLdsRequestCache } from "../components/lds/cache";

const defaultFamiliesUiState = {
  searchQuery: "",
  onlyEmpty: false,
  onlyNoChildren: false,
  onlyNoParents: false,
  sortEmptyFirst: true,
  scrollY: 0,
};

const defaultChildrenUiState = {
  search: "",
  onlyMultiFamilies: false,
  onlyWithoutFamily: false,
  showAlumni: false,
  showDeletionRequested: false,
  scrollY: 0,
};

const familiesCache = createLdsMemoryCache();
const familiesUiStateCache = createLdsMemoryCache(defaultFamiliesUiState);
const loadFamiliesOverviewOnce = createLdsRequestCache();
const familyDetailsCache = new Map();

const childrenCache = createLdsMemoryCache();
const alumniChildrenCache = createLdsMemoryCache();
const loadChildrenOverviewOnce = createLdsRequestCache();
const loadAlumniChildrenOverviewOnce = createLdsRequestCache();
const childrenUiStateCache = createLdsMemoryCache(defaultChildrenUiState);
const childDetailsCache = new Map();
const classroomsCache = createLdsMemoryCache();
const loadClassroomsOverviewOnce = createLdsRequestCache();

export const hasFamiliesCache = () => familiesCache.has();
export const getCachedFamilies = () => familiesCache.get();
export const getFamiliesUiState = () => familiesUiStateCache.get() ?? defaultFamiliesUiState;

export const setFamiliesUiState = (state) => {
  familiesUiStateCache.set({
    ...getFamiliesUiState(),
    ...state,
  });
};

export const setCachedFamilies = (families) => {
  const safeFamilies = Array.isArray(families) ? families : [];
  familiesCache.set(safeFamilies);
  safeFamilies.forEach((family) => {
    if (!family?.id) return;
    const familyId = String(family.id);
    const existingDetails = familyDetailsCache.get(familyId);
    if (existingDetails) {
      familyDetailsCache.set(familyId, {
        ...existingDetails,
        family: { ...existingDetails.family, ...family },
        updatedAt: Date.now(),
      });
    }
  });
};

export const removeCachedFamily = (familyId) => {
  const id = String(familyId);
  familiesCache.update((families) => families?.filter((family) => String(family.id) !== id) ?? families);
  familyDetailsCache.delete(id);
};

export const upsertCachedFamily = (family) => {
  if (!family?.id) return;
  const familyId = String(family.id);
  const cachedFamilies = familiesCache.get();
  if (cachedFamilies) {
    const existingIndex = cachedFamilies.findIndex((item) => String(item.id) === familyId);
    familiesCache.set(
      existingIndex >= 0
        ? cachedFamilies.map((item, index) => (index === existingIndex ? { ...item, ...family } : item))
        : [family, ...cachedFamilies]
    );
  }

  const existingDetails = familyDetailsCache.get(familyId);
  if (existingDetails) {
    familyDetailsCache.set(familyId, {
      ...existingDetails,
      family: { ...existingDetails.family, ...family },
      updatedAt: Date.now(),
    });
  }
};

export const getCachedFamily = (familyId) => {
  const id = String(familyId);
  return (
    familyDetailsCache.get(id)?.family ??
    familiesCache.get()?.find((family) => String(family.id) === id) ??
    null
  );
};

export const getCachedFamilyDetails = (familyId) => familyDetailsCache.get(String(familyId)) ?? null;

export const setCachedFamilyDetails = (familyId, entry) => {
  familyDetailsCache.set(String(familyId), {
    ...entry,
    updatedAt: Date.now(),
  });
  if (entry?.family) upsertCachedFamily(entry.family);
};

export const loadFamiliesOverview = async () => {
  return loadFamiliesOverviewOnce(async () => {
      const { data } = await http.get("/families");
      const families = Array.isArray(data) ? data : [];
      setCachedFamilies(families);
      return families;
  });
};

export const hasChildrenCache = () => childrenCache.has();
export const hasAlumniChildrenCache = () => alumniChildrenCache.has();
export const getCachedChildren = () => childrenCache.get();
export const getCachedAlumniChildren = () => alumniChildrenCache.get();
export const getChildrenUiState = () => childrenUiStateCache.get() ?? defaultChildrenUiState;

export const setChildrenUiState = (state) => {
  childrenUiStateCache.set({
    ...getChildrenUiState(),
    ...state,
  });
};

export const setCachedChildren = (children) => {
  childrenCache.set(Array.isArray(children) ? children : []);
};

export const setCachedAlumniChildren = (children) => {
  alumniChildrenCache.set(Array.isArray(children) ? children : []);
};

export const getCachedChild = (childId) => {
  const id = String(childId);
  return (
    childDetailsCache.get(id)?.child ??
    childrenCache.get()?.find((child) => String(child.id) === id) ??
    alumniChildrenCache.get()?.find((child) => String(child.id) === id) ??
    null
  );
};

export const upsertCachedChild = (child) => {
  if (!child?.id) return;
  const childId = String(child.id);
  const cachedChildren = childrenCache.get();
  if (cachedChildren) {
    const existingIndex = cachedChildren.findIndex((item) => String(item.id) === childId);
    if (existingIndex >= 0) {
      childrenCache.set(cachedChildren.map((item, index) => (index === existingIndex ? { ...item, ...child } : item)));
    }
  }
  const cachedAlumniChildren = alumniChildrenCache.get();
  if (cachedAlumniChildren) {
    const existingIndex = cachedAlumniChildren.findIndex((item) => String(item.id) === childId);
    if (existingIndex >= 0) {
      alumniChildrenCache.set(cachedAlumniChildren.map((item, index) => (index === existingIndex ? { ...item, ...child } : item)));
    }
  }

  const existingDetails = childDetailsCache.get(childId);
  if (existingDetails) {
    childDetailsCache.set(childId, {
      ...existingDetails,
      child: { ...existingDetails.child, ...child },
      updatedAt: Date.now(),
    });
  }
};

export const removeCachedChild = (childId) => {
  const id = String(childId);
  childrenCache.update((children) => children?.filter((child) => String(child.id) !== id) ?? children);
  alumniChildrenCache.update((children) => children?.filter((child) => String(child.id) !== id) ?? children);
  childDetailsCache.delete(id);
};

export const moveCachedChildToAlumni = (child) => {
  if (!child?.id) return;
  const id = String(child.id);
  childrenCache.update((children) => children?.filter((item) => String(item.id) !== id) ?? children);
  alumniChildrenCache.update((children) => {
    if (!children) return [child];
    const existingIndex = children.findIndex((item) => String(item.id) === id);
    return existingIndex >= 0
      ? children.map((item, index) => (index === existingIndex ? { ...item, ...child } : item))
      : [child, ...children];
  });
  const existingDetails = childDetailsCache.get(id);
  if (existingDetails) {
    childDetailsCache.set(id, {
      ...existingDetails,
      child: { ...existingDetails.child, ...child },
      updatedAt: Date.now(),
    });
  }
};

export const getCachedChildDetails = (childId) => childDetailsCache.get(String(childId)) ?? null;

export const setCachedChildDetails = (childId, entry) => {
  childDetailsCache.set(String(childId), {
    ...entry,
    updatedAt: Date.now(),
  });
  if (entry?.child) upsertCachedChild(entry.child);
};

export const loadChildrenOverview = async () => {
  return loadChildrenOverviewOnce(async () => {
      const { data } = await http.get("/children");
      const children = Array.isArray(data) ? data : [];
      setCachedChildren(children);
      return children;
  });
};

export const loadAlumniChildrenOverview = async () => {
  return loadAlumniChildrenOverviewOnce(async () => {
      const { data } = await http.get("/children", { params: { status: "alumni" } });
      const children = Array.isArray(data) ? data : [];
      setCachedAlumniChildren(children);
      return children;
  });
};

export const hasClassroomsCache = () => classroomsCache.has();
export const getCachedClassrooms = () => classroomsCache.get();

export const setCachedClassrooms = (classrooms) => {
  classroomsCache.set(Array.isArray(classrooms) ? classrooms : []);
};

export const loadClassroomsOverview = async () => {
  return loadClassroomsOverviewOnce(async () => {
      const { data } = await http.get("/classrooms");
      const classrooms = Array.isArray(data) ? data : [];
      setCachedClassrooms(classrooms);
      return classrooms;
  });
};
